import { useEffect, useMemo, useState, type ReactElement } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';
import { LogOut } from 'lucide-react';
import { can, type SessionMembership, type SessionResponse } from '@kratispot/shared';
import { AppHeader } from '@/components/app-header';
import { HeaderMenu } from '@/components/header-menu';
import { InstallBanner } from '@/components/install-banner';
import { VenuePicker } from '@/components/venue-picker';
import { Button } from '@/components/ui/button';
import { useHealth } from '@/hooks/use-health';
import { useSession } from '@/hooks/use-session';
import { logout } from '@/lib/api';
import { currentVenue, readVenue, rememberVenue, VENUE_PARAM } from '@/lib/current-venue';
import { surfaceFor } from '@/lib/hosts';
import { LanguageProvider, useTranslation } from '@/lib/i18n';
import { BookingPage } from '@/pages/booking-page';
import { LandingPage } from '@/pages/landing-page';
import { CheckoutPage } from '@/pages/checkout-page';
import { ReturnPage } from '@/pages/return-page';
import { JoinPage } from '@/pages/join-page';
import { LoginPage } from '@/pages/login-page';
import { PlatformPage } from '@/pages/platform-page';
import { LayoutPage } from '@/pages/layout-page';
import { SettingsPage } from '@/pages/settings-page';
import { ActivityPage } from '@/pages/activity-page';
import { TeamPage } from '@/pages/team-page';
import { paramsOf, VenuePage } from '@/pages/venue-page';
import { VisitorPage } from '@/pages/visitor-page';

// Only an active membership puts a venue on the app's list of places to work.
export function workableVenues(session: SessionResponse): SessionMembership[] {
  return session.memberships.filter((membership) => membership.status === 'active');
}

// The address bar decides which of the two surfaces this is; see lib/hosts. §21
function Shell(): ReactElement {
  const staff = surfaceFor(window.location.hostname, useLocation().pathname) === 'staff';
  return staff ? <StaffRoutes /> : <PublicRoutes />;
}

function StaffRoutes(): ReactElement {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/join" element={<JoinPage />} />
      <Route path="*" element={<SignedIn />} />
    </Routes>
  );
}

// The apex root is a branded landing until Phase 8 opens discovery here. §21
function PublicRoutes(): ReactElement {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      {/* Before :providerRef — a real provider redirects here, naming no booking. */}
      <Route path="/checkout/return" element={<ReturnPage />} />
      <Route path="/checkout/:providerRef" element={<CheckoutPage />} />
      <Route path="/:slug" element={<VisitorPage />} />
      <Route path="/:slug/booking" element={<BookingPage />} />
    </Routes>
  );
}

export function App(): ReactElement {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </LanguageProvider>
  );
}

function SignedIn(): ReactElement {
  const { t } = useTranslation();
  const health = useHealth();
  const { state, reload } = useSession();
  // §8: the venue is in the address as the day is, and remembered besides.
  const [query, setQuery] = useSearchParams();
  const asked = query.get(VENUE_PARAM);
  const [picked, setPicked] = useState<string | null>(null);
  const navigate = useNavigate();
  // Whose venue is remembered: two accounts on one phone keep their own. §4
  const userId = state.phase === 'signed-in' ? state.session.user.id : null;
  const remembered = useMemo(() => (userId === null ? null : readVenue(userId)), [userId]);

  useEffect(() => {
    if (userId === null || asked === null || asked === picked) return;
    setPicked(asked);
    rememberVenue(userId, asked);
  }, [asked, picked, userId]);

  if (state.phase === 'checking') return <Centred>{t('loading')}</Centred>;
  if (state.phase === 'signed-out') return <Navigate to="/login" replace />;
  // §20: the shell stands offline, and says so rather than blaming itself.
  if (state.phase === 'offline' || state.phase === 'unreachable')
    return (
      <Centred>
        {state.phase === 'offline' && <p className="font-semibold">{t('offlineTitle')}</p>}
        <p>{t(state.phase === 'offline' ? 'offlineLead' : 'serverDown')}</p>
        <Button variant="outline" onClick={reload}>
          {t('tryAgain')}
        </Button>
      </Centred>
    );

  const venues = workableVenues(state.session);
  const current = currentVenue(venues, asked, picked ?? remembered);

  // The picker names a venue; the address and this device both keep it.
  function pick(slug: string): void {
    setPicked(slug);
    if (userId !== null) rememberVenue(userId, slug);
    setQuery((was) => ({ ...paramsOf(was), [VENUE_PARAM]: slug }));
  }

  return (
    <div className="flex h-full flex-col">
      <InstallBanner />
      <AppHeader health={health}>
        {current !== undefined && (
          <>
            <VenuePicker
              memberships={venues}
              venueId={current.venueId}
              viaPlatform={current.viaPlatform}
              onPick={pick}
            />
            <HeaderMenu role={current.role} operator={state.session.user.isSuperadmin} />
          </>
        )}
        <Button
          size="sm"
          variant="outline"
          className="shrink-0"
          aria-label={t('signOut')}
          onClick={() => void logout().then(reload)}
        >
          <LogOut className="size-4 sm:hidden" aria-hidden />
          <span className="hidden sm:inline">{t('signOut')}</span>
        </Button>
      </AppHeader>
      <main className="flex flex-1 flex-col overflow-auto">
        {current === undefined ? (
          <Centred>{t('noVenue')}</Centred>
        ) : (
          <Routes>
            <Route
              path="/team"
              element={
                current.role === 'owner' ? (
                  <TeamPage venueId={current.venueId} />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/settings"
              element={
                can({ role: current.role, status: 'active' }, 'venue:settings') ? (
                  <SettingsPage venueId={current.venueId} />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/layout"
              element={
                can({ role: current.role, status: 'active' }, 'venue:layout') ? (
                  <LayoutPage venueId={current.venueId} venues={venues} />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/activity"
              element={
                can({ role: current.role, status: 'active' }, 'reservation:activity') ? (
                  <ActivityPage venueId={current.venueId} />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/platform"
              element={
                state.session.user.isSuperadmin ? (
                  <PlatformPage
                    onVenuesChanged={reload}
                    onOpenVenue={(slug) => {
                      setPicked(slug);
                      if (userId !== null) rememberVenue(userId, slug);
                      void navigate(`/?${VENUE_PARAM}=${slug}`);
                    }}
                  />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="*"
              element={
                <VenuePage
                  venueId={current.venueId}
                  membership={{ role: current.role, status: 'active' }}
                />
              }
            />
          </Routes>
        )}
      </main>
    </div>
  );
}

function Centred({ children }: { children: React.ReactNode }): ReactElement {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}
